import { useMemo } from 'react';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import AppError from '../../../../app/features/error/services/AppError';
import _ from 'lodash';
import IFundAsset from '../../interfaces/fund/IFundAsset';
import { useTranslation } from 'react-i18next';

const useInvestFundAssets = (fundId: number | null) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();
	const { i18n } = useTranslation();
	const filter = {
		fundId: fundId,
		userId: auth.authOverrideUserId,
	};

	const { data, isFetching } = useQuery<IFundAsset[], Error>(
		['investmentFundAssets', filter, i18n.language],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($fundId: Int) {
							investmentFundAssets(fundId: $fundId) {
								name
								descriptionText
								imgSrc
								currency
								countryCode
								sectorNameText
								investment {
									date
									maturityDate
									contributedCapital
									outstandingCapital
									multiple
									percentOwned
									reportingPeriod {
										tag
										name
										year
										number
									}
									reportingDate
									reportingDistribution
									reportingFMV
									reportingStatus
								}
							}
						}
					`,
					filter,
				)) as { investmentFundAssets: IFundAsset[] };
				return data.investmentFundAssets;
			} catch (e) {
				throw new AppError(e, 'useInvestFundAssets');
			}
		},
		{
			enabled:
				!!filter.fundId && auth.currentCompany !== null && auth.isInvestor === true && auth.isAuthenticated,
		},
	);

	const fundAssets = useMemo(() => (data ? _.orderBy(data, ['investment.date'], ['asc']) : null), [data]);

	return {
		isFetching,
		fundAssets,
	};
};

export default useInvestFundAssets;
